import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TabsContent } from "@/components/ui/tabs";
import { EmptyState, StatusBadge, inr } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";

export function ExitTab({ exits, onRefresh }) {
  const [settleFor, setSettleFor] = useState(null);
  const [fnf, setFnf] = useState("");

  const exitAction = async (id, path, body = {}) => {
    try {
      await api.post(`/employer/exits/${id}/${path}`, body);
      toast.success("Exit workflow updated");
      setSettleFor(null);
      setFnf("");
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <TabsContent value="exit">
      {exits.length === 0 ? (
        <EmptyState title="No exit processes" description="Resignations and full & final settlements will appear here." testId="exits-empty" />
      ) : (
        <div className="space-y-4" data-testid="exits-list">
          {exits.map((x) => (
            <div key={x.id} className="bg-white border border-border/60 rounded-xl p-5" data-testid={`exit-${x.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-sm text-[#0B1B3D]">{x.name} — {x.designation}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {x.reason || "No reason given"} · {fmtDate(x.created_at)}
                    {x.pending_assets > 0 && <span className="text-orange-600 font-semibold"> · {x.pending_assets} asset(s) pending return</span>}
                    {x.fnf_amount > 0 && ` · F&F: ${inr(x.fnf_amount)}`}
                  </p>
                </div>
                <StatusBadge status={x.status} />
              </div>
              <div className="flex gap-2 mt-4 flex-wrap">
                {x.status === "resigned" && (
                  <>
                    <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => exitAction(x.id, "review", { action: "approve" })} data-testid={`exit-approve-${x.id}`}>Approve Resignation</Button>
                    <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => exitAction(x.id, "review", { action: "reject", note: "Discussed with HR" })} data-testid={`exit-reject-${x.id}`}>Reject</Button>
                  </>
                )}
                {x.status === "notice_period" && (
                  <>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => exitAction(x.id, "clearance")} data-testid={`exit-clearance-${x.id}`}>Start Clearance</Button>
                    <Button size="sm" className="rounded-full bg-[#0B1B3D]" onClick={() => setSettleFor(x)} data-testid={`exit-settle-${x.id}`}>Process F&F</Button>
                  </>
                )}
                {x.status === "clearance" && (
                  <Button size="sm" className="rounded-full bg-[#0B1B3D]" onClick={() => setSettleFor(x)} data-testid={`exit-settle2-${x.id}`}>Process F&F</Button>
                )}
                {x.status === "fnf_processed" && (
                  <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => exitAction(x.id, "relieve")} data-testid={`exit-relieve-${x.id}`}>Relieve & Issue Letters</Button>
                )}
              </div>
              {x.history?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-border/50 flex gap-4 flex-wrap">
                  {x.history.map((h, i) => (
                    <span key={i} className="text-[11px] text-muted-foreground capitalize">
                      {h.action.replace(/_/g, " ")} · {fmtDate(h.at)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!settleFor} onOpenChange={() => setSettleFor(null)}>
        <DialogContent aria-describedby={undefined} data-testid="fnf-dialog">
          <DialogHeader><DialogTitle>Full & Final — {settleFor?.name}</DialogTitle></DialogHeader>
          {settleFor?.pending_assets > 0 && (
            <p className="text-xs bg-orange-50 border border-orange-200 text-orange-700 rounded-lg px-3 py-2">
              {settleFor.pending_assets} asset(s) are pending return. F&F requires asset clearance.
            </p>
          )}
          <div>
            <Label>F&F settlement amount (₹)</Label>
            <Input type="number" value={fnf} onChange={(e) => setFnf(e.target.value)} className="mt-1.5" data-testid="fnf-amount" />
          </div>
          <Button onClick={() => exitAction(settleFor.id, "settle", { fnf_amount: Number(fnf) })} disabled={!fnf} className="w-full rounded-full bg-[#0B1B3D]" data-testid="fnf-submit-btn">
            Process Settlement
          </Button>
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
}
